import type { OrderModel } from 'src/types/order';
import type { MediaModel } from 'src/types/media';
import type { CustomerModel } from 'src/types/customer';

import dayjs from 'dayjs';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import { useRef, useMemo, useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  Stack,
  Table,
  Dialog,
  Button,
  Tooltip,
  TableRow,
  TextField,
  TableBody,
  TableCell,
  TableHead,
  IconButton,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  TableContainer,
  InputAdornment,
} from '@mui/material';

import { useMedias } from 'src/hooks/medias/useMedias';
import { useDebounce } from 'src/hooks/common/useDebounce';
import { useCustomers } from 'src/hooks/customers/useCustomers';
import { useCategories } from 'src/hooks/categories/use-categories';

import { fDate } from 'src/utils/format-time';
import { fCurrency } from 'src/utils/format-number';

import { countries } from 'src/assets/data';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

import { LinkTypeEnum } from 'src/types/media';

import OrderBilling from './order-new-edit-billing';
import OrderDetails from './order-new-edit-details';
import { useOrderItems } from './hooks/use-order-items';
import { useBillingForm } from './hooks/use-billing-form';
import { useOrderSubmit } from './hooks/use-order-submit';
import { useOrderPreload } from './hooks/use-order-preload';
import OrderBillingDetails from './order-new-edit-billing-details';
import { NewOrderSchema, PaymentMethodEnum } from './types/order-form.types';

import type { NewOrderSchemaType } from './types/order-form.types';

// Re-export for backward compatibility with other components
export { PaymentMethodEnum } from './types/order-form.types';
export type {
  BillingSchemaType,
  NewOrderSchemaType,
  CombinedBillingType,
  PlatformBillingSchemaType,
} from './types/order-form.types';

type OrderNewEditFormProps = {
  currentOrder?: OrderModel;
};

// Referencia estable para cuando `medias.data` aún no ha llegado (SWR devuelve `undefined`
// mientras la key de búsqueda está en vuelo, algo que pasa en cada tecleo del buscador de
// medio). Un `medias?.data || []` inline crea un array nuevo en cada render mientras dura esa
// carga; como ese array se pasa como `mediaOptions` a OrderDetails y es dependencia de un
// useEffect que ahí dentro llama a setValue('price', ...) sobre un campo observado en este
// mismo formulario, cada setValue disparaba un re-render que volvía a crear el array — bucle
// infinito ("Maximum update depth exceeded") al buscar un medio en el pedido externo nuevo.
const EMPTY_MEDIAS: MediaModel[] = [];

export function OrderNewEditForm({ currentOrder }: OrderNewEditFormProps) {
  const isEditMode = !!currentOrder;

  // =============================================
  // Media & Customer search
  // =============================================
  const [searchMedia, setSearchMedia] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodEnum>(PaymentMethodEnum.Pending);

  const debouncedSearchMedia = useDebounce(searchMedia, 500);

  const { medias, isLoading } = useMedias(1, 50, {
    mediaId: undefined,
    name: '',
    email: '',
    withoutEmail: false,
    linkType: null,
    mediaType: null,
    categories: [],
    country: [],
    province: '',
    language: '',
    redactionField: null,
    active: true,
    drMin: null,
    drMax: null,
    daMin: null,
    daMax: null,
    tfMin: null,
    tfMax: null,
    trafficMin: null,
    trafficMax: null,
    priceMin: null,
    priceMax: null,
    url: '',
    searchTerm: debouncedSearchMedia || undefined,
  });

  const { data: customerOptions, loading: loadingCustomers } = useCustomers({
    searchQuery: searchCustomer,
  });

  const { data: allCategories, loading: loadingCategories } = useCategories();

  // =============================================
  // Order form (details)
  // =============================================
  const defaultLimitDate = useMemo(() => dayjs().add(7, 'day'), []);

  const defaultValues = useMemo(
    () => ({
      mediaId: undefined,
      price: 0,
      currency: 0,
      limitDate: defaultLimitDate,
      // Default category id 1 = "Activismo y ONG" — ahorra clics al equipo, modificable.
      categoryId: 1,
      finalUrl: '',
      // Default true — la inmensa mayoría de pedidos externos requieren redacción.
      requireRedaction: true,
      extraRedaction: false,
      wordCount: 600,
      // Default DoFollow en linkType 1 y 2 — el equipo casi siempre los pone DoFollow.
      // Why: Else pidió que arranquen ya con DoFollow seleccionado, sin que el usuario tenga
      // que abrir el desplegable. 3 y 4 quedan vacíos a propósito.
      linkType1: LinkTypeEnum.DoFollow,
      linkType2: LinkTypeEnum.DoFollow,
    }),
    [defaultLimitDate]
  );

  const methods = useForm<NewOrderSchemaType>({
    resolver: zodResolver(NewOrderSchema),
    defaultValues,
  });

  const { watch, setValue } = methods;
  const watchedMediaField = watch('mediaId');
  const wordCount = watch('wordCount');
  // Solo se usan para pintar el resumen de "Detalles del Pedido" en modo edición (1 fila, sin carrito).
  const watchedPrice = watch('price');
  const watchedLimitDate = watch('limitDate');
  const watchedFinalUrl = watch('finalUrl');
  // En modo edición no hay carrito (orderItems siempre está vacío), así que la card "Facturación"
  // se quedaba con el mensaje de "Añade items al pedido" pese a haber un pedido real. Estos se
  // usan para construir un item sintético con los datos en vivo del pedido y así sí calcular el
  // resumen (subtotal/total/beneficio) igual que en creación.
  const watchedCurrency = watch('currency');
  const watchedCostPrice = watch('costPrice');
  const watchedCostPriceCurrency = watch('costPriceCurrency');
  const watchedCategoryCommission = watch('categoryCommission');
  const watchedDiscountPercentage = watch('discountPercentage');

  // =============================================
  // Billing form
  // =============================================
  const { billingMethod, setBillingMethod, billingMethods, validateBillingForm } = useBillingForm();

  // =============================================
  // Order items (create mode cart)
  // =============================================
  const {
    orderItems,
    editingItemIndex,
    onAddOrUpdateItem,
    handleRemoveItem,
    handleEditItem,
    handleCancelEdit,
    applyGlobalDiscount,
    resetForm: resetItemForm,
  } = useOrderItems(methods, medias?.data ?? EMPTY_MEDIAS);

  // Item sintético con los datos en vivo del pedido, solo para que "Facturación" tenga algo
  // que sumar en modo edición (ver comentario en los watch de más arriba).
  // Mientras se teclea, los campos numéricos del formulario llegan aquí como string (el
  // Controller de RHFTextField solo los convierte a number en el blur), y OrderBilling hace
  // aritmética y .toFixed() directamente sobre estos valores: Number(...) evita pasarle un string.
  const billingItems = useMemo(() => {
    if (!isEditMode) return orderItems;
    return [
      {
        mediaName: watchedMediaField?.label || currentOrder?.normalDetails?.media?.name || '',
        price: Number(watchedPrice) || 0,
        currency: watchedCurrency ?? 0,
        finalUrl: watchedFinalUrl || '',
        wordCount: wordCount || 0,
        costPrice: Number(watchedCostPrice) || 0,
        costPriceCurrency: watchedCostPriceCurrency,
        categoryCommission: Number(watchedCategoryCommission) || 0,
        discountPercentage: Number(watchedDiscountPercentage) || 0,
      },
    ];
  }, [
    isEditMode,
    orderItems,
    watchedMediaField,
    currentOrder,
    watchedPrice,
    watchedCurrency,
    watchedFinalUrl,
    wordCount,
    watchedCostPrice,
    watchedCostPriceCurrency,
    watchedCategoryCommission,
    watchedDiscountPercentage,
  ]);

  // =============================================
  // Popup para añadir/editar un medio del carrito (creación) o para editar los detalles
  // del pedido (edición de un pedido ya existente, que no tiene concepto de carrito).
  // No se cierra al clicar fuera ni con Esc: es un formulario largo, un cierre accidental
  // perdería lo rellenado.
  // =============================================
  const [itemDialogOpen, setItemDialogOpen] = useState(false);

  // Un intento previo fallido de Guardar deja errores en formState.errors; sin este clearErrors
  // quedaban visibles la próxima vez que se abre el popup (para añadir uno nuevo, editar otro
  // item o editar el pedido), como si ya se hubiera intentado guardar sin haber tecleado nada.
  const openItemDialog = () => {
    methods.clearErrors();
    setItemDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (!isEditMode) {
      if (editingItemIndex >= 0) {
        handleCancelEdit();
      } else {
        resetItemForm();
      }
    }
    setItemDialogOpen(false);
  };

  const handleOpenEditItemDialog = (index: number) => {
    handleEditItem(index);
    openItemDialog();
  };

  const handleSaveItemDialog = async () => {
    const isValid = await methods.trigger();
    if (!isValid) {
      // Los campos en rojo ya señalan cuáles fallan, pero con un formulario tan largo eso
      // puede pasar desapercibido si el campo que falla no está a la vista; el toast avisa
      // aunque haya que hacer scroll para verlo.
      toast.error('Es necesario rellenar los campos obligatorios');
      return;
    }
    if (!isEditMode) {
      await onAddOrUpdateItem();
      setItemDialogOpen(false);
      return;
    }
    // En modo edición no hay "añadir al carrito": el único guardado real de un pedido ya
    // existente es el PUT completo (submitOrder, el mismo que dispara "Actualizar Pedido").
    // Antes "Guardar" aquí solo cerraba el popup sin mandar nada al backend, y si el usuario
    // no pulsaba también "Actualizar Pedido" después, lo editado en el popup (ej. una imagen
    // recién subida) se perdía sin ningún aviso. Ahora "Guardar" sí guarda de verdad.
    setItemDialogOpen(false);
    await submitOrder();
  };

  // Descuento global aplicable a todos los medios del pedido.
  const [globalDiscount, setGlobalDiscount] = useState<number | ''>('');

  // =============================================
  // Pre-load data in edit mode
  // =============================================
  useOrderPreload({
    currentOrder,
    isEditMode,
    methods,
    medias: medias?.data ?? EMPTY_MEDIAS,
    setBillingMethod,
    setPaymentMethod,
    setSearchMedia,
  });

  // =============================================
  // Auto-fill costPrice from selected media
  // =============================================
  // Depende del id (watchedMediaField?.value), no del objeto `medias?.data` entero: ese array
  // cambia de referencia cada vez que useMedias vuelve a pedir el listado (ej. el preload de
  // edición busca el medio del pedido por nombre nada más abrir el formulario), y sin este
  // cambio el efecto se disparaba con cada refetch aunque el medio seleccionado no cambiara,
  // pisando en cualquier momento — incluso mientras el admin estaba escribiendo — el Precio
  // Coste ya cargado del pedido (o lo que el admin acababa de teclear) con el coste por defecto
  // del medio.
  useEffect(() => {
    if (!watchedMediaField?.value) return;

    const selectedMedia = medias?.data?.find((media) => media.id === watchedMediaField.value);

    if (selectedMedia?.costPrice !== undefined && selectedMedia.costPrice !== null) {
      setValue('costPrice', selectedMedia.costPrice);
    }
    if (
      selectedMedia?.costPriceCurrency !== undefined &&
      selectedMedia.costPriceCurrency !== null
    ) {
      setValue('costPriceCurrency', selectedMedia.costPriceCurrency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedMediaField?.value, setValue]);

  // =============================================
  // Submit handler
  // =============================================
  const { handleSubmit: submitOrder, isProcessing } = useOrderSubmit({
    isEditMode,
    currentOrder,
    orderItems,
    billingMethod,
    paymentMethod,
    methods,
    validateBillingForm,
    billingMethods,
  });

  // =============================================
  // Customer selection handler
  // =============================================
  const handleCustomerSelect = (customer: CustomerModel | null) => {
    if (!customer) return;

    const { setValue: setBillingValue } = billingMethods;

    setBillingValue('customerId', customer.id);
    setBillingValue('firstName', customer.name || '');
    setBillingValue('lastName', customer.surname || '');
    setBillingValue('company', customer.company || '');
    setBillingValue('email', customer.email || '');
    setBillingValue('phone', customer.phone || '');
    setBillingValue('nif', customer.nif || '');

    if (customer.country) {
      const foundCountry = countries.find(
        (c) => c.label.toLowerCase() === customer.country?.toLowerCase()
      );
      setBillingValue('country', foundCountry?.label || customer.country);
    } else {
      setBillingValue('country', '');
    }

    setBillingValue('city', customer.city || '');
    setBillingValue('postalCode', customer.postalCode || '');
    setBillingValue('address', customer.address || '');
  };

  // =============================================
  // Autofill por email al crear pedido externo (facturación por email)
  // Al terminar de escribir un email que coincide EXACTO con un cliente ya existente, se
  // autorrellenan los demás campos, sin esperar a que el admin use el buscador "Cambiar cliente".
  // Solo en creación: en modo edición los datos ya vienen precargados del pedido.
  // =============================================
  const watchedBillingEmail = billingMethods.watch('email');
  const debouncedBillingEmail = useDebounce(watchedBillingEmail, 500);
  const looksLikeCompleteEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(debouncedBillingEmail || '');
  const emailLookupQuery =
    !isEditMode && billingMethod === 'email' && looksLikeCompleteEmail ? debouncedBillingEmail : '';

  const { data: emailMatchOptions } = useCustomers({ searchQuery: emailLookupQuery });

  // Evita repetir el autorrelleno (y el aviso) para el mismo email una y otra vez mientras el
  // admin sigue editando otros campos del formulario.
  const autofilledEmailRef = useRef<string | null>(null);

  useEffect(() => {
    if (!emailLookupQuery) return;

    const normalizedEmail = emailLookupQuery.toLowerCase();
    if (autofilledEmailRef.current === normalizedEmail) return;

    const match = (emailMatchOptions ?? []).find((c) => c.email?.toLowerCase() === normalizedEmail);
    if (!match) return;

    autofilledEmailRef.current = normalizedEmail;
    handleCustomerSelect(match);
    toast.info(
      `Cliente encontrado: datos autocompletados (${[match.name, match.surname].filter(Boolean).join(' ')})`
    );
    // handleCustomerSelect no está memoizado y cambia en cada render; solo debe re-ejecutarse
    // cuando cambian el email buscado o los resultados de esa búsqueda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailLookupQuery, emailMatchOptions]);

  // =============================================
  // Render
  // =============================================
  return (
    <>
      {/* Todo en filas a ancho completo en vez de dos columnas: Items del Pedido arriba (la
          tabla respira mejor a lo ancho y ya no hace falta igualar su alto con el de al lado),
          Detalles de Facturación + Facturación en una fila debajo (paneles más pequeños, van
          mejor uno junto al otro que sueltos a ancho completo), y el botón de enviar al final. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Edit mode header */}
        {isEditMode && (
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Editando Pedido #{currentOrder?.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Modifica los detalles del pedido y haz clic en &quot;Actualizar Pedido&quot; para
              guardar los cambios.
            </Typography>
          </Card>
        )}

        {/* En creación: carrito de items del pedido, vacío al principio.
            En edición: resumen (1 fila) del pedido ya existente, sin concepto de carrito. */}
        <Card sx={{ p: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">
              {isEditMode ? 'Detalles del Pedido' : 'Items del Pedido'}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              {/* Descuento global a todos los medios (creación, con items ya añadidos) */}
              {!isEditMode && orderItems.length > 0 && (
                <>
                  <TextField
                    size="small"
                    type="number"
                    label="% Descuento global"
                    value={globalDiscount}
                    onChange={(e) =>
                      setGlobalDiscount(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    sx={{ width: 170 }}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: { min: 0, max: 100, step: 0.1 },
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => applyGlobalDiscount(Number(globalDiscount) || 0)}
                  >
                    Aplicar a todos
                  </Button>
                </>
              )}

              {!isEditMode && (
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                  onClick={openItemDialog}
                >
                  Añadir medio
                </Button>
              )}
            </Stack>
          </Stack>

          {isEditMode ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Medio</TableCell>
                    <TableCell>Precio</TableCell>
                    <TableCell>Fecha Limite</TableCell>
                    <TableCell>Url Final</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{watchedMediaField?.label || '—'}</TableCell>
                    <TableCell>{fCurrency(watchedPrice || 0)}</TableCell>
                    <TableCell>
                      {watchedLimitDate ? fDate(watchedLimitDate) : '-'}
                    </TableCell>
                    <TableCell>{watchedFinalUrl ? 'Si' : 'No'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={openItemDialog}>
                          <Iconify icon="solar:pen-bold" width={18} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : orderItems.length === 0 ? (
            <Box
              sx={{
                minHeight: 160,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Aún no has añadido ningún medio a este pedido.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Medio</TableCell>
                    <TableCell>Precio</TableCell>
                    <TableCell>Descuento</TableCell>
                    <TableCell>Fecha Limite</TableCell>
                    <TableCell>Url Final</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderItems.map((item, index) => {
                    const basePrice = item.price + (item.categoryCommission || 0);
                    const dto = item.discountPercentage || 0;
                    const finalPrice =
                      item.price * (1 - dto / 100) + (item.categoryCommission || 0);
                    return (
                      <TableRow key={index}>
                        <TableCell>{item.mediaName}</TableCell>
                        <TableCell>
                          {dto > 0 ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.disabled', textDecoration: 'line-through' }}
                              >
                                {fCurrency(basePrice)}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {fCurrency(finalPrice)}
                              </Typography>
                            </Stack>
                          ) : (
                            fCurrency(basePrice)
                          )}
                        </TableCell>
                        <TableCell>
                          {dto > 0 ? (
                            <Typography
                              variant="body2"
                              sx={{ color: 'error.main', fontWeight: 600 }}
                            >
                              −{dto}%
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>{item.limitDate ? fDate(item.limitDate) : '-'}</TableCell>
                        <TableCell>{item.finalUrl ? 'Si' : 'No'}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditItemDialog(index)}
                              >
                                <Iconify icon="solar:pen-bold" width={18} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(item)}
                              >
                                <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* Detalles de Facturación y Facturación, cada uno a ancho completo en su propia fila.
            Probado en 50/50 uno junto al otro y no funciona: "Detalles de Facturación" se
            alarga mucho al elegir Email (deja alturas descuadradas), y la tabla de "Facturación"
            tiene 7-8 columnas que no caben en la mitad de la pantalla sin scroll horizontal. */}
        <OrderBillingDetails
          billingMethods={billingMethods}
          billingMethod={billingMethod}
          onBillingMethodChange={setBillingMethod}
          customerOptions={customerOptions || []}
          loadingCustomers={loadingCustomers || false}
          onSearchCustomer={setSearchCustomer}
          onCustomerSelect={handleCustomerSelect}
          onBillingSubmit={submitOrder}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={setPaymentMethod}
          isProcessing={isProcessing}
        />

        <OrderBilling
          orderItems={billingItems}
          paymentMethod={paymentMethod}
          wordCount={wordCount}
          onRemoveItem={isEditMode ? undefined : handleRemoveItem}
          billingCountry={billingMethods.watch('country')}
        />

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <LoadingButton
            variant="contained"
            onClick={submitOrder}
            disabled={(isEditMode ? false : orderItems.length === 0) || isProcessing}
            loading={isProcessing}
          >
            {isEditMode ? 'Actualizar Pedido' : 'Crear Pedido'}
          </LoadingButton>
        </Stack>
      </Box>

      {/* Popup para añadir/editar un medio (creación) o los detalles del pedido (edición).
          Sin cierre al clicar fuera ni con Esc: solo desde los botones. */}
      <Dialog
        open={itemDialogOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleDialogClose();
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        {/* FormProvider (contexto puro, sin <form> real) en vez de <Form>: un <form> de por
            medio entre DialogTitle/DialogContent/DialogActions rompe el layout flex interno
            del Dialog de MUI (se veía una franja rota debajo de los botones). No hace falta
            <form> porque Guardar valida y envía por onClick, no por submit nativo. */}
        <FormProvider {...methods}>
          <DialogTitle sx={{ pb: 1 }}>
            {isEditMode
              ? 'Editar detalles del pedido'
              : editingItemIndex >= 0
                ? 'Editar medio del pedido'
                : 'Añadir medio al pedido'}
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 3, pb: 3 }}>
            <OrderDetails
              embedded
              methods={methods}
              mediaOptions={medias?.data ?? EMPTY_MEDIAS}
              loadingMedia={isLoading}
              categoryOptions={allCategories ?? []}
              loadingCategories={loadingCategories}
              onSearchMedia={setSearchMedia}
              selectedPlatformId={
                billingMethod === 'platform' ? billingMethods.watch('platformId')?.value : undefined
              }
              isEditMode={isEditMode}
              isEditingItem={editingItemIndex >= 0}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button color="inherit" onClick={handleDialogClose}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color={!isEditMode && editingItemIndex >= 0 ? 'warning' : 'primary'}
              onClick={handleSaveItemDialog}
            >
              {isEditMode
                ? 'Guardar'
                : editingItemIndex >= 0
                  ? 'Guardar Cambios'
                  : 'Añadir al carrito'}
            </Button>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </>
  );
}
